import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { getEmpresasByCA } from '../../calls'
import { fetchInstallers } from '../../../prescriptor-installers/slices/prescriptor-installers.slice'
import { MARKETGAS_URL } from '../../constants'
import { feedback } from '../../../../core/feedback'
import EmpresaInfo from './empresa-info.component'
import EmpresaSelector from './empresa-selector.component'
import { AutocompleteInput } from '../../../../ui'
import { useTokenGenerator } from '../../hooks/useTokenGenerator'
import { useConsultContext } from '../../contexts'
import { postProvisionalApp } from '../../calls'
import { assembleTmpAppFromState } from '../../logics/object-assembling.logics'

export const EmpresaBlock = ({ home, empresa, setEmpresa }) => {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.global)
  const [empresas, setEmpresas] = useState([])
  const [isLoadingEmpresas, setIsLoadingEmpresas] = useState(false)
  const combos = useSelector((state) => state.combos)
  const prescriptorInstallers = useSelector((state) => state.prescriptorInstallers)
  const intl = useIntl()
  const { generateOneUseLink } = useTokenGenerator()
  const { checkIfAppExist, createApplication, reset, customerForm } = useConsultContext()
  const [isRedirecting, setIsRedirecting] = useState(false)

  const isPrescriptor = user.role === 'administrador_prescriptor'

  const cc_aa = useMemo(() => {
    return combos['provincias']?.data.find((el) => el.cp === home.cod_postal?.substring(0, 2))?.ca
  }, [home, combos])

  // Carga empresa normal (no prescriptor)
  useEffect(() => {
    if (isPrescriptor) return
    if (!cc_aa) return

    if (user.empresa_cc_aa && user.empresa_cc_aa.length) {
      const ownEmpresa = user.empresa_cc_aa.find((el) => '' + el.id_cc_aa === '' + cc_aa)
      if (ownEmpresa) {
        setEmpresa({
          ...ownEmpresa,
          nombre: ownEmpresa.nombre_empresa,
          telefono: ownEmpresa.telefono,
          empresa_ca_id: ownEmpresa.id_empresa,
        })
        setEmpresas([])
      }
    } else if (user.gremio_id) {
      setIsLoadingEmpresas(true)
      setEmpresas([])
      getEmpresasByCA(user.gremio_id, cc_aa)
        .then(({ data }) => {
          setEmpresas(
            data.empresas
              .filter((el) => el.aceptacion_gremio)
              .map((el) => ({ ...el, empresa_ca_id: el.id }))
          )
        })
        .catch((err) => {
          feedback(
            'error',
            'Error al cargar las empresas disponibles. Por favor, intenta de nuevo.'
          )
          setEmpresas([])
        })
        .finally(() => {
          setIsLoadingEmpresas(false)
        })
    }
  }, [cc_aa, user.gremio_id, user.empresa_cc_aa, isPrescriptor]) // eslint-disable-line react-hooks/exhaustive-deps

  // Carga instaladores del prescriptor
  useEffect(() => {
    if (!isPrescriptor) return
    if (!user.empresa_id) return
    if (!prescriptorInstallers.data || prescriptorInstallers.data.length === 0) {
      dispatch(fetchInstallers({ activo: true }, user.empresa_id))
    }
  }, [isPrescriptor, user.empresa_id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Bloque para el prescriptor
  if (isPrescriptor) {
    const isSVOrES = home?.mercado === 'SV' || home?.mercado === 'ES'
    const activeInstallers = (prescriptorInstallers.data || []).filter(
      (el) => el.activo !== false && '' + el.comunidad_autonoma === '' + cc_aa
    )
    const isLoading = prescriptorInstallers.isLoading

    const handleInstallerChange = (choice) => {
      if (!choice?.value) {
        setEmpresa(null)
        return
      }
      const installer = choice.value
      setEmpresa({
        id: installer.id,
        nombre: installer.nombre,
        telefono: installer.telefono || '',
        empresa_ca_id: installer.empresa_ca_id,
        cif: installer.cif,
        email: installer.email,
      })
    }

    // Valida existencia, crea solicitud provisional, genera token de acceso y redirige a MarketGas
    const handleGoMarketGas = async () => {
      if (!home?.cidi) return
      setIsRedirecting(true)

      try {
        await checkIfAppExist(home.cidi).catch((err) => {
          throw new Error('La solicitud ya existe o hay un error de validación.')
        })

        const consultState = {
          customerForm: { ...customerForm, uso: customerForm.uso || 'residencial' },
          selectedHome: home,
          certificateForm: {},
        }

        const tmpApp = await postProvisionalApp(
          assembleTmpAppFromState(consultState, isPrescriptor, user.empresa_id)
        ).catch((err) => {
          throw new Error('No se pudo crear la solicitud provisional.')
        })

        if (!tmpApp?.id) {
          throw new Error('No se pudo obtener el ID de la solicitud provisional.')
        }

        const { data } = await generateOneUseLink(user.user_id, 'prescriptor', tmpApp.id, {
          ...home,
          uso: customerForm.uso || 'residencial',
        })

        if (data?.message) {
          window.open(`${MARKETGAS_URL}?root=3&id=${data.message}`, '_blank', 'noopener,noreferrer')
        }

        reset()
      } catch (error) {
        console.error('Error en el flujo de MarketGas:', error)
      } finally {
        setIsRedirecting(false)
      }
    }

    return (
      <>
        <Typography variant="h4" color="textSecondary" paragraph>
          {intl.formatMessage({ id: 'pages.consult.prescriptor.empresa.title' })}
        </Typography>
        <Box p={3} mt={3} bgcolor="#f5f5f5" borderRadius={2}>
          {isLoading ? (
            <Typography variant="body2" color="textSecondary">
              Cargando instaladores...
            </Typography>
          ) : activeInstallers.length === 0 ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="textSecondary">
                {intl.formatMessage({ id: 'pages.consult.prescriptor.empresa.noInstaller.msg' })}
              </Typography>
              {isSVOrES && (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={handleGoMarketGas}
                    disabled={isRedirecting}
                  >
                    {intl.formatMessage({
                      id: 'pages.consult.prescriptor.empresa.noInstaller.altAction',
                    })}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <AutocompleteInput
                title={intl.formatMessage({ id: 'pages.consult.prescriptor.empresa.select' })}
                name="instalador"
                optionKey="nombre"
                values={activeInstallers}
                value={empresa}
                onChange={handleInstallerChange}
              />
              {empresa && empresa.nombre ? (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">
                    <strong>Nombre:</strong> {empresa.nombre}
                  </Typography>
                  {empresa.telefono ? (
                    <Typography variant="body2">
                      <strong>Teléfono:</strong> {empresa.telefono}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
              {!empresa && isSVOrES && (
                <Box mt={1} display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={handleGoMarketGas}
                    disabled={isRedirecting}
                  >
                    {intl.formatMessage({
                      id: 'pages.consult.prescriptor.empresa.noInstaller.altAction',
                    })}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </>
    )
  }

  // Bloque normal (no prescriptor)
  return (
    <>
      <Typography variant="h4" color="textSecondary" paragraph>
        Empresa
      </Typography>
      <Box p={3} mt={3} bgcolor="#f5f5f5" borderRadius={2}>
        <Box display="flex" flexDirection="column">
          <EmpresaInfo empresa={empresa} empresas={empresas} isLoading={isLoadingEmpresas} />
          {user.empresa || !empresas.length ? null : (
            <EmpresaSelector empresa={empresa} setEmpresa={setEmpresa} empresas={empresas} />
          )}
        </Box>
      </Box>{' '}
    </>
  )
}

export default EmpresaBlock
